/**
 * RichTextEditor - Full-featured WYSIWYG editor for blog posts and forum threads
 * Similar to SoapMakingFriend's post editor
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { sanitizeHtml, safeContentUrl, escapeHtml } from '../../utils/sanitizeHtml';
import './RichTextEditor.css';

// Toolbar button component
const ToolbarButton = ({ icon, title, onClick, active, disabled }) => (
  <button
    type="button"
    className={`rte-btn ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    disabled={disabled}
  >
    {icon}
  </button>
);

// Dropdown component for formatting options
const ToolbarDropdown = ({ value, options, onChange, title }) => (
  <select
    className="rte-dropdown"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onMouseDown={(e) => e.stopPropagation()}
    title={title}
  >
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Write your content here...',
  minHeight = 300,
  onImageUpload,
  allowImages = true,
  allowPolls = false,
  allowAttachments = true,
}) {
  const editorRef = useRef(null);
  const imageFileInputRef = useRef(null);
  const attachFileInputRef = useRef(null);
  const isInternalChange = useRef(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Set initial content and sync when value changes externally
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      // Only update DOM if value actually differs (external change)
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = sanitizeHtml(value);
      }
    }
    isInternalChange.current = false;
  }, [value]);

  // Handle content changes
  const handleContentChange = useCallback(() => {
    if (onChange && editorRef.current) {
      isInternalChange.current = true;
      onChange(sanitizeHtml(editorRef.current.innerHTML));
    }
  }, [onChange]);

  const execCommand = useCallback((command, val = null) => {
    document.execCommand(command, false, command === 'insertHTML' ? sanitizeHtml(val) : val);
    editorRef.current?.focus();
    handleContentChange();
  }, [handleContentChange]);

  // Format block (heading, paragraph, etc)
  const handleFormatBlock = (tag) => {
    execCommand('formatBlock', tag);
  };

  // Insert link
  const handleInsertLink = () => {
    if (linkUrl) {
      const url = safeContentUrl(linkUrl);
      if (!url) { alert('Enter an https://, http://, mailto:, or relative link.'); return; }
      const text = linkText || linkUrl;
      execCommand('insertHTML', `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`);
    }
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  };

  // Handle image file selection
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Insert image
  const handleInsertImage = async () => {
    let imgSrc = imageUrl;
    
    if (imageFile && onImageUpload) {
      try {
        // Upload image and get URL
        imgSrc = await onImageUpload(imageFile);
      } catch (error) {
        console.error('Image upload failed:', error);
        // Fallback to base64 preview if upload fails
        if (imagePreview) {
          imgSrc = imagePreview;
        } else {
          alert('Failed to upload image');
          return;
        }
      }
    } else if (imagePreview && !imageUrl) {
      // Use base64 preview if no upload handler
      imgSrc = imagePreview;
    }

    if (imgSrc) {
      const safeSrc = safeContentUrl(imgSrc, { image: true });
      if (!safeSrc) { alert('Enter a valid image URL.'); return; }
      execCommand('insertHTML', `<img src="${escapeHtml(safeSrc)}" alt="Uploaded image" style="max-width: 100%; height: auto; margin: 8px 0;" />`);
    }
    
    setShowImageModal(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview(null);
  };

  // Insert emoji
  const emojis = ['😀', '😍', '🎉', '👍', '❤️', '🔥', '✨', '🌟', '💡', '🧼', '🛁', '💧', '🌿', '🌸', '🍋', '🥥'];
  const handleInsertEmoji = (emoji) => {
    execCommand('insertText', emoji);
    setShowEmojiPicker(false);
  };

  // Insert horizontal rule
  const handleInsertHR = () => {
    execCommand('insertHorizontalRule');
  };

  // Insert quote
  const handleInsertQuote = () => {
    execCommand('formatBlock', 'blockquote');
  };

  // Insert code block
  const handleInsertCode = () => {
    execCommand('insertHTML', '<pre><code>// Your code here</code></pre>');
  };

  // Insert table
  const handleInsertTable = () => {
    const tableHTML = `
      <table style="border-collapse: collapse; width: 100%; margin: 12px 0;">
        <tr>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 1</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 2</th>
          <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Header 3</th>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 1</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 2</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 3</td>
        </tr>
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 4</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 5</td>
          <td style="border: 1px solid #ddd; padding: 8px;">Cell 6</td>
        </tr>
      </table>
    `;
    execCommand('insertHTML', tableHTML);
  };

  // Clear formatting
  const handleClearFormat = () => {
    execCommand('removeFormat');
  };

  return (
    <div className="rich-text-editor">
      {/* Toolbar */}
      <div className="rte-toolbar">
        {/* Text formatting */}
        <div className="rte-toolbar-group">
          <ToolbarButton icon="✏️" title="Clear Formatting" onClick={handleClearFormat} />
          <ToolbarButton icon={<b>B</b>} title="Bold (Ctrl+B)" onClick={() => execCommand('bold')} />
          <ToolbarButton icon={<i>I</i>} title="Italic (Ctrl+I)" onClick={() => execCommand('italic')} />
          <ToolbarButton icon={<u>U</u>} title="Underline (Ctrl+U)" onClick={() => execCommand('underline')} />
          <ToolbarButton icon={<s>S</s>} title="Strikethrough" onClick={() => execCommand('strikeThrough')} />
        </div>

        <div className="rte-separator" />

        {/* Font formatting */}
        <div className="rte-toolbar-group">
          <ToolbarDropdown
            value="p"
            title="Format"
            options={[
              { value: 'p', label: 'Paragraph' },
              { value: 'h1', label: 'Heading 1' },
              { value: 'h2', label: 'Heading 2' },
              { value: 'h3', label: 'Heading 3' },
              { value: 'h4', label: 'Heading 4' },
            ]}
            onChange={handleFormatBlock}
          />
        </div>

        <div className="rte-separator" />

        {/* Lists */}
        <div className="rte-toolbar-group">
          <ToolbarButton icon="⦁" title="Bullet List" onClick={() => execCommand('insertUnorderedList')} />
          <ToolbarButton icon="1." title="Numbered List" onClick={() => execCommand('insertOrderedList')} />
          <ToolbarButton icon="❝" title="Quote Block" onClick={handleInsertQuote} />
        </div>

        <div className="rte-separator" />

        {/* Alignment */}
        <div className="rte-toolbar-group">
          <ToolbarButton icon="⫷" title="Align Left" onClick={() => execCommand('justifyLeft')} />
          <ToolbarButton icon="☰" title="Align Center" onClick={() => execCommand('justifyCenter')} />
          <ToolbarButton icon="⫸" title="Align Right" onClick={() => execCommand('justifyRight')} />
        </div>

        <div className="rte-separator" />

        {/* Insert elements */}
        <div className="rte-toolbar-group">
          <ToolbarButton icon="🔗" title="Insert Link" onClick={() => setShowLinkModal(true)} />
          {allowImages && (
            <ToolbarButton icon="🖼️" title="Insert Image" onClick={() => setShowImageModal(true)} />
          )}
          <ToolbarButton icon="📊" title="Insert Table" onClick={handleInsertTable} />
          <ToolbarButton icon="—" title="Horizontal Line" onClick={handleInsertHR} />
          <ToolbarButton icon="</>" title="Code Block" onClick={handleInsertCode} />
        </div>

        <div className="rte-separator" />

        {/* Extras */}
        <div className="rte-toolbar-group">
          <div className="rte-emoji-wrapper">
            <ToolbarButton icon="😊" title="Insert Emoji" onClick={() => setShowEmojiPicker(!showEmojiPicker)} />
            {showEmojiPicker && (
              <div className="rte-emoji-picker">
                {emojis.map(emoji => (
                  <button key={emoji} type="button" onClick={() => handleInsertEmoji(emoji)}>{emoji}</button>
                ))}
              </div>
            )}
          </div>
          <ToolbarButton icon="↩️" title="Undo" onClick={() => execCommand('undo')} />
          <ToolbarButton icon="↪️" title="Redo" onClick={() => execCommand('redo')} />
        </div>

        {/* Preview toggle */}
        <div className="rte-toolbar-right">
          <span className="rte-preview-label">📄 Preview</span>
        </div>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        dir="ltr"
        onInput={handleContentChange}
        onBlur={handleContentChange}
        onPaste={(event) => {
          event.preventDefault();
          const html = event.clipboardData.getData('text/html');
          if (html) execCommand('insertHTML', html);
          else execCommand('insertText', event.clipboardData.getData('text/plain'));
        }}
        onDrop={(event) => {
          event.preventDefault();
          const html = event.dataTransfer.getData('text/html');
          if (html) execCommand('insertHTML', html);
          else execCommand('insertText', event.dataTransfer.getData('text/plain'));
        }}
        onKeyDown={(e) => {
          // Ensure Enter creates a new paragraph, not just a <br> in some browsers
          if (e.key === 'Enter' && !e.shiftKey) {
            // Let the browser handle it, but ensure we're in a block element
            const sel = window.getSelection();
            if (sel?.rangeCount) {
              const node = sel.anchorNode;
              const parentBlock = node?.nodeType === 3 ? node.parentElement : node;
              // If typing directly in the editor root, wrap in a <p>
              if (parentBlock === editorRef.current) {
                e.preventDefault();
                document.execCommand('insertParagraph', false);
                handleContentChange();
              }
            }
          }
        }}
        data-placeholder={placeholder}
        style={{ minHeight }}
      />

      {/* Link Modal */}
      {showLinkModal && (
        <div className="rte-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="rte-modal" onClick={e => e.stopPropagation()}>
            <h3>🔗 Insert Link</h3>
            <div className="rte-modal-field">
              <label>URL</label>
              <input
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div className="rte-modal-field">
              <label>Link Text (optional)</label>
              <input
                type="text"
                placeholder="Click here"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
            </div>
            <div className="rte-modal-actions">
              <button type="button" className="cancel" onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button type="button" className="primary" onClick={handleInsertLink}>Insert Link</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="rte-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="rte-modal" onClick={e => e.stopPropagation()}>
            <h3>🖼️ Insert Image</h3>
            
            <div className="rte-image-tabs">
              <button 
                type="button" 
                className={!imageUrl && !imageFile ? 'active' : ''}
                onClick={() => { setImageUrl(''); setImageFile(null); setImagePreview(null); }}
              >
                Upload
              </button>
              <button 
                type="button" 
                className={imageUrl ? 'active' : ''}
                onClick={() => { setImageFile(null); setImagePreview(null); }}
              >
                URL
              </button>
            </div>

            {!imageUrl ? (
              <div className="rte-image-upload">
                <input
                  ref={imageFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  style={{ display: 'none' }}
                />
                <div 
                  className="rte-upload-zone"
                  onClick={() => imageFileInputRef.current?.click()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file?.type.startsWith('image/')) {
                      setImageFile(file);
                      const reader = new FileReader();
                      reader.onload = (e) => setImagePreview(e.target.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="rte-image-preview" />
                  ) : (
                    <div className="rte-upload-placeholder">
                      <span className="rte-upload-icon">📷</span>
                      <span>Click or drag image here</span>
                      <span className="rte-upload-hint">JPG, PNG, GIF up to 5MB</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="rte-modal-field">
                <label>Image URL</label>
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
                {imageUrl && (
                  <img src={imageUrl} alt="Preview" className="rte-image-preview" />
                )}
              </div>
            )}

            <div className="rte-modal-actions">
              <button type="button" className="cancel" onClick={() => setShowImageModal(false)}>Cancel</button>
              <button 
                type="button" 
                className="primary" 
                onClick={handleInsertImage}
                disabled={!imageUrl && !imageFile && !imagePreview}
              >
                Insert Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachments area */}
      {allowAttachments && (
        <div className="rte-attachments">
          <input
            ref={attachFileInputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx,.txt"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.type.startsWith('image/')) {
                // For images, insert directly into editor
                if (onImageUpload) {
                  try {
                    const url = await onImageUpload(file);
                    editorRef.current?.focus();
                    document.execCommand('insertHTML', false, `<img src="${url}" alt="${file.name}" style="max-width: 100%; height: auto; margin: 8px 0;" />`);
                    handleContentChange();
                  } catch (err) {
                    console.error('Upload failed:', err);
                    alert('Failed to upload file.');
                  }
                } else {
                  // Fallback: insert as base64
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    editorRef.current?.focus();
                    document.execCommand('insertHTML', false, `<img src="${ev.target.result}" alt="${file.name}" style="max-width: 100%; height: auto; margin: 8px 0;" />`);
                    handleContentChange();
                  };
                  reader.readAsDataURL(file);
                }
              } else {
                alert('Only image files can be inserted into the editor.');
              }
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
          <button type="button" className="rte-attach-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => attachFileInputRef.current?.click()}>
            📎 Attach files
          </button>
        </div>
      )}
    </div>
  );
}
